import { t } from '@lingui/core/macro'
import { AuthedAxiosRequests } from 'features/auth'
import { forecastMath, type Matrix } from 'features/math'
import { CamelCase, RegressionConfig } from 'types'
import { z } from 'zod'

export interface RegressionResult {
  coefficients: number[]
  predictions: Array<{
    timestamp: number
    value: number
  }>
  pVals?: number[] // p-values for coefficients (if applicable)
  r2?: number // R-squared value
  equation?: string
  // Additional fields for multivariate regression
  featureNames?: string[]
  // Normalization parameters for polynomial regression
  normalizationParams?: {
    minTime: number
    maxTime: number
    timeMid: number
    timeRange: number
  }
}

const RegressionApiResponseSchema = z
  .object({
    coefficients: z.array(z.number()),
    predictions: z.array(z.number()).optional().or(z.null()),
    p_vals: z.array(z.number()),
    r2: z.number(),
    equation: z.string(),
  })
  .catchall(z.unknown())

interface LinearRegressionApiPayload {
  features: number[][]
  values: number[]
  predict_features?: number[][]
}

/**
 * Performs multivariate linear regression using the time series service API
 * y = β₀ + β₁x₁ + β₂x₂ + ... + βₙxₙ
 * Multiple indepndent variables, one dependent variable
 */
export async function performMultivariateLinearRegression(
  features: number[][],
  values: number[],
  timeSeriesServiceUrl: string,
  authorizedAxios: AuthedAxiosRequests,
  predictFeatures?: number[][]
): Promise<CamelCase<z.infer<typeof RegressionApiResponseSchema>>> {
  const payload: LinearRegressionApiPayload = {
    features,
    values,
  }

  if (predictFeatures && predictFeatures.length > 0) {
    payload.predict_features = predictFeatures
  }

  const response = await authorizedAxios.post(
    `${timeSeriesServiceUrl}/api/v1/model/regression/linear`,
    payload
  )

  const parsedResponse = RegressionApiResponseSchema.parse(response.data)

  return {
    coefficients: parsedResponse.coefficients,
    predictions: parsedResponse.predictions,
    pVals: parsedResponse.p_vals,
    r2: parsedResponse.r2,
    equation: parsedResponse.equation,
  }
}

/**
 * Performs polynomial regression on time series data
 * y = a0 + a1*x + a2*x^2 + ... + an*x^n
 */
export function performPolynomialRegression(
  timestamps: number[],
  values: number[],
  degree: number,
  futureTimestamps: number[]
): RegressionResult {
  const n = timestamps.length
  if (n < degree + 1) {
    throw new Error(
      `Need at least ${degree + 1} data points for degree ${degree} polynomial`
    )
  }

  // Normalize timestamps to [-1, 1] range for numerical stability
  // Use reduce to avoid stack overflow with large datasets
  const minTime = timestamps.reduce(
    (min, t) => (t < min ? t : min),
    timestamps[0]
  )
  const maxTime = timestamps.reduce(
    (max, t) => (t > max ? t : max),
    timestamps[0]
  )
  const timeRange = maxTime - minTime
  const timeMid = (maxTime + minTime) / 2

  // Map timestamps to [-1, 1] range
  const x = timestamps.map((t) =>
    timeRange > 0 ? (2 * (t - timeMid)) / timeRange : 0
  )

  // Build Vandermonde matrix with normalized values
  const X = x.map((xi) => {
    const row: number[] = []
    for (let i = 0; i <= degree; i++) {
      row.push(Math.pow(xi, i))
    }
    return row
  })

  // Solve using mathjs with QR decomposition for numerical stability
  const matrixX = forecastMath.matrix(X)
  const vectorY = forecastMath.matrix(values)

  // Use QR decomposition: X = QR
  // Then solve Rx = Q'y
  const qr = forecastMath.qr(matrixX)
  const Q = qr.Q
  const R = qr.R

  // Calculate Q'y
  const Qty = forecastMath.multiply(forecastMath.transpose(Q), vectorY)

  // Extract the upper square portion of R (n×n) and first n elements of Q'y
  // where n is the number of coefficients (degree + 1)
  const n_coef = degree + 1
  const R_square = forecastMath.subset(
    R,
    forecastMath.index(
      forecastMath.range(0, n_coef),
      forecastMath.range(0, n_coef)
    )
  )
  const Qty_subset = forecastMath.subset(
    Qty,
    forecastMath.index(forecastMath.range(0, n_coef))
  )

  // Solve the square system R_square * x = Qty_subset
  const coefficients = forecastMath
    .lusolve(R_square as Matrix, Qty_subset)
    .toArray()
    .flat() as number[]

  // Calculate R-squared
  const yMean = values.reduce((a, b) => a + b, 0) / n
  const ssTotal = values.reduce((sum, yi) => sum + Math.pow(yi - yMean, 2), 0)
  const ssResidual = values.reduce((sum, yi, i) => {
    let yPred = 0
    for (let j = 0; j <= degree; j++) {
      yPred += coefficients[j] * Math.pow(x[i], j)
    }
    return sum + Math.pow(yi - yPred, 2)
  }, 0)
  const r2 = 1 - ssResidual / ssTotal

  // Generate predictions using normalized timestamps
  const predictions = futureTimestamps.map((t) => {
    // Normalize future timestamp the same way
    const tNorm = timeRange > 0 ? (2 * (t - timeMid)) / timeRange : 0
    let value = 0
    for (let i = 0; i <= degree; i++) {
      value += coefficients[i] * Math.pow(tNorm, i)
    }
    return { timestamp: t, value }
  })

  // Build equation string with normalized time notation
  const terms = coefficients
    .map((coef, i) => {
      const absCoef = Math.abs(coef)
      if (absCoef < 1e-10) return '' // Skip near-zero coefficients

      const sign = coef < 0 ? '-' : i === 0 ? '' : '+'
      const coefStr = absCoef.toFixed(4)

      if (i === 0) return `${sign}${coefStr}`
      if (i === 1) return `${sign} ${coefStr}t`
      return t`${sign} ${coefStr}t^${i}`
    })
    .filter((term) => term !== '')
    .join(' ')

  const equation = t`y = ${terms} (t normalized to [-1,1])`
  return {
    coefficients,
    predictions,
    r2,
    equation,
    normalizationParams: {
      minTime,
      maxTime,
      timeMid,
      timeRange,
    },
  }
}

/**
 * Performs logistic regression on time series data
 * y = L / (1 + e^(-k(x-x0)))
 * where L is the maximum value, k is the growth rate, x0 is the midpoint
 */
export async function performLogisticRegression(
  timestamps: number[],
  values: number[],
  futureTimestamps: number[],
  authorizedAxios: AuthedAxiosRequests,
  timeSeriesServiceUrl: string
): Promise<RegressionResult> {
  const n = timestamps.length
  if (n < 3) {
    throw new Error('Need at least 3 data points for logistic regression')
  }

  // Use timestamps directly
  const x = timestamps

  // Estimate initial parameters
  const maxY = values.reduce((max, v) => (v > max ? v : max), values[0])
  const L = maxY * 1.1 // Slightly above max as asymptote

  // Transform to linear form: ln((L-y)/y) = -k*x + k*x0
  // Filter out values too close to L to avoid numerical issues
  const validIndices: number[] = []
  const transformedY: number[] = []
  const validX: number[] = []

  for (let i = 0; i < n; i++) {
    if (values[i] > 0 && values[i] < L * 0.99) {
      const transformed = Math.log((L - values[i]) / values[i])
      if (isFinite(transformed)) {
        transformedY.push(transformed)
        validX.push(x[i])
        validIndices.push(i)
      }
    }
  }

  if (validX.length < 2) {
    // Fallback to linear if logistic transform fails
    // Use multivariate regression with time as the feature
    const features = timestamps.map((t) => [t])
    const futureFeatures = futureTimestamps.map((t) => [t])
    const result = await performMultivariateLinearRegression(
      features,
      values,
      timeSeriesServiceUrl,
      authorizedAxios,
      futureFeatures
    )

    // Generate time-based predictions
    const predictionValues = result.predictions

    const predictions = predictionValues
      ? futureTimestamps.map((t, i) => ({
          timestamp: t,
          value: predictionValues[i],
        }))
      : []

    return {
      ...result,
      predictions,
      equation: result.equation?.replace('x1', 't'),
    }
  }

  // Linear regression on transformed data
  const sumX = validX.reduce((a, b) => a + b, 0)
  const sumY = transformedY.reduce((a, b) => a + b, 0)
  const sumXY = validX.reduce((sum, xi, i) => sum + xi * transformedY[i], 0)
  const sumX2 = validX.reduce((sum, xi) => sum + xi * xi, 0)
  const nValid = validX.length

  const slope = (nValid * sumXY - sumX * sumY) / (nValid * sumX2 - sumX * sumX)
  const intercept = (sumY - slope * sumX) / nValid

  const k = -slope
  const x0 = intercept / k

  // Calculate R-squared on original scale
  const yMean = values.reduce((a, b) => a + b, 0) / n
  const ssTotal = values.reduce((sum, yi) => sum + Math.pow(yi - yMean, 2), 0)
  const ssResidual = values.reduce((sum, yi, i) => {
    const yPred = L / (1 + Math.exp(-k * (x[i] - x0)))
    return sum + Math.pow(yi - yPred, 2)
  }, 0)
  const r2 = 1 - ssResidual / ssTotal

  // Generate predictions
  const predictions = futureTimestamps.map((t) => {
    const value = L / (1 + Math.exp(-k * (t - x0)))
    return { timestamp: t, value }
  })

  return {
    coefficients: [L, k, x0],
    predictions,
    r2,
    equation: t`y = ${{ value315_23: L.toFixed(2) }} / (1 + e^(-${{ value315_50: k.toFixed(2) }}(x - ${{ value315_70: x0.toFixed(2) }})))`,
  }
}

/**
 * Performs sinusoidal regression on time series data
 * y = A * sin(B * x + C) + D
 * where A is amplitude, B is frequency, C is phase shift, D is vertical shift
 */
export function performSinusoidalRegression(
  timestamps: number[],
  values: number[],
  futureTimestamps: number[]
): RegressionResult {
  const n = timestamps.length
  if (n < 4) {
    throw new Error('Need at least 4 data points for sinusoidal regression')
  }

  // Use timestamps directly
  const x = timestamps

  // Estimate initial parameters
  const minY = values.reduce((min, v) => (v < min ? v : min), values[0])
  const maxY = values.reduce((max, v) => (v > max ? v : max), values[0])

  // D: vertical shift (mean of data)
  const D = values.reduce((sum, v) => sum + v, 0) / n

  // A: amplitude (half the range)
  const A_initial = (maxY - minY) / 2

  // Estimate frequency using zero-crossings
  const centeredValues = values.map((v) => v - D)
  let zeroCrossings = 0
  for (let i = 1; i < n; i++) {
    if (centeredValues[i - 1] * centeredValues[i] < 0) {
      zeroCrossings++
    }
  }

  // B: frequency (2π * number of periods / range)
  // Number of periods ≈ zeroCrossings / 2
  const periods = Math.max(0.5, zeroCrossings / 2)
  const B_initial = 2 * Math.PI * periods

  // Use iterative optimization to refine parameters
  let A = A_initial
  let B = B_initial
  let C = 0

  // Gradient descent optimization
  const learningRate = 0.1
  const iterations = 100

  for (let iter = 0; iter < iterations; iter++) {
    // Calculate gradients
    let dA = 0,
      dB = 0,
      dC = 0

    for (let i = 0; i < n; i++) {
      const predicted = A * Math.sin(B * x[i] + C) + D
      const residual = values[i] - predicted

      // Gradients (negative because we're minimizing error)
      dA += residual * Math.sin(B * x[i] + C)
      dB += residual * A * x[i] * Math.cos(B * x[i] + C)
      dC += residual * A * Math.cos(B * x[i] + C)
    }

    // Update parameters
    A += (learningRate * dA) / n
    B += (learningRate * dB) / n
    C += (learningRate * dC) / n

    // Early stopping if converged
    if (Math.abs(dA) + Math.abs(dB) + Math.abs(dC) < 0.001) {
      break
    }
  }

  // Ensure positive amplitude
  if (A < 0) {
    A = -A
    C = C + Math.PI
  }

  // Normalize phase to [-π, π]
  while (C > Math.PI) C -= 2 * Math.PI
  while (C < -Math.PI) C += 2 * Math.PI

  // Calculate R-squared
  const yMean = values.reduce((a, b) => a + b, 0) / n
  const ssTotal = values.reduce((sum, yi) => sum + Math.pow(yi - yMean, 2), 0)
  const ssResidual = values.reduce((sum, yi, i) => {
    const yPred = A * Math.sin(B * x[i] + C) + D
    return sum + Math.pow(yi - yPred, 2)
  }, 0)
  const r2 = 1 - ssResidual / ssTotal

  // Generate predictions
  const predictions = futureTimestamps.map((t) => {
    const value = A * Math.sin(B * t + C) + D
    return { timestamp: t, value }
  })

  // Calculate period in original time units
  const periodMs = (2 * Math.PI) / B
  const periodDays = periodMs / (1000 * 60 * 60 * 24)

  return {
    coefficients: [A, B, C, D],
    predictions,
    r2,
    equation: t`y = ${{ value430_23: A.toFixed(2) }} * sin(${{ value430_45: B.toFixed(2) }}x + ${{ value430_64: C.toFixed(2) }}) + ${{ value430_83: D.toFixed(2) }} (period: ${{ value430_108: periodDays.toFixed(1) }} days)`,
  }
}

/**
 * Main regression function that delegates to specific implementations
 */
export async function performRegression(
  timestamps: number[],
  values: number[],
  config: RegressionConfig,
  timeSeriesServiceUrl: string,
  authorizedAxios: AuthedAxiosRequests
): Promise<RegressionResult> {
  // Filter out null/undefined values
  const validPairs = timestamps
    .map((t, i) => ({ timestamp: t, value: values[i] }))
    .filter(
      (pair) =>
        pair.value !== null && pair.value !== undefined && !isNaN(pair.value)
    )

  if (validPairs.length < 2) {
    throw new Error('Insufficient valid data points for regression')
  }

  const validTimestamps = validPairs.map((p) => p.timestamp)
  const validValues = validPairs.map((p) => p.value)

  // Generate timestamps for predictions
  let futureTimestamps: number[] = []

  if (
    config.minX !== undefined ||
    config.maxX !== undefined ||
    config.endDate
  ) {
    // Use minX/maxX if provided, otherwise fall back to endDate
    const dataMin = validTimestamps.reduce(
      (min, t) => (t < min ? t : min),
      validTimestamps[0]
    )

    const dataMax = validTimestamps.reduce(
      (max, t) => (t > max ? t : max),
      validTimestamps[0]
    )

    const minX = config.minX ?? dataMin
    const maxX =
      config.maxX ??
      (config.endDate ? new Date(config.endDate).getTime() : dataMax)

    // Generate evenly spaced points between minX and maxX
    const numPoints = 200
    futureTimestamps = []
    for (let i = 0; i <= numPoints; i++) {
      const timestamp = minX + (maxX - minX) * (i / numPoints)
      futureTimestamps.push(timestamp)
    }
  }

  switch (config.regressionType) {
    case 'linear': {
      let features: number[][]

      if (
        config.xVariables &&
        config.xVariables.length > 0 &&
        config.xVariables[0] !== 'timestamp'
      ) {
        throw new Error(
          'Linear regression with non-timestamp independent variable should be handled by the caller'
        )
      } else {
        features = validTimestamps.map((t) => [t])
      }

      const result = await performMultivariateLinearRegression(
        features,
        validValues,
        timeSeriesServiceUrl,
        authorizedAxios
      )

      let predictions: Array<{ timestamp: number; value: number }> = []
      if (futureTimestamps.length > 0) {
        const futureFeatures = futureTimestamps.map((t) => [t])
        const predictionResult = await performMultivariateLinearRegression(
          features,
          validValues,
          timeSeriesServiceUrl,
          authorizedAxios,
          futureFeatures
        )

        if (predictionResult.predictions) {
          predictions = futureTimestamps.map((t, i) => ({
            timestamp: t,
            value: predictionResult.predictions![i],
          }))
        }
      }

      const updatedEquation = result.equation?.replace('x1', 't')

      return {
        ...result,
        predictions,
        equation: updatedEquation,
        featureNames: [t`Time`],
      }
    }

    case 'polynomial':
      return performPolynomialRegression(
        validTimestamps,
        validValues,
        config.degree || 2,
        futureTimestamps
      )

    case 'logistic':
      return performLogisticRegression(
        validTimestamps,
        validValues,
        futureTimestamps,
        authorizedAxios,
        timeSeriesServiceUrl
      )

    case 'sinusoidal':
      return performSinusoidalRegression(
        validTimestamps,
        validValues,
        futureTimestamps
      )

    default:
      throw new Error(`Unknown regression type: ${config.type}`)
  }
}

/**
 * Predicts values for given timestamps using a regression result
 * @param regressionResult The result from performing regression
 * @param config The regression configuration
 * @param timestamps Array of timestamps to predict values for
 * @returns Array of predicted values (or null for invalid cases)
 */
export function predictValues(
  regressionResult: RegressionResult,
  config: RegressionConfig,
  timestamps: number[]
): (number | null)[] {
  return timestamps.map((timestamp) => {
    // First check if we already have a prediction for this timestamp
    const existingPrediction = regressionResult.predictions.find(
      (p) => p.timestamp === timestamp
    )
    if (existingPrediction) {
      return existingPrediction.value
    }

    // Otherwise, calculate using the regression equation
    switch (config.regressionType) {
      case 'linear': {
        // For linear regression: y = a0 + a1*x
        if (regressionResult.coefficients.length >= 2) {
          return (
            regressionResult.coefficients[0] +
            regressionResult.coefficients[1] * timestamp
          )
        }
        return null
      }

      case 'polynomial': {
        // For polynomial regression, we need to normalize the timestamp
        if (!regressionResult.normalizationParams) {
          // Fallback if normalization params are missing
          let value = 0
          for (let i = 0; i < regressionResult.coefficients.length; i++) {
            value += regressionResult.coefficients[i] * Math.pow(timestamp, i)
          }
          return value
        }

        const { timeMid, timeRange } = regressionResult.normalizationParams
        const tNorm =
          timeRange > 0 ? (2 * (timestamp - timeMid)) / timeRange : 0

        let value = 0
        for (let i = 0; i < regressionResult.coefficients.length; i++) {
          value += regressionResult.coefficients[i] * Math.pow(tNorm, i)
        }
        return value
      }

      case 'logistic': {
        // For logistic regression: y = L / (1 + e^(-k(x-x0)))
        if (regressionResult.coefficients.length >= 3) {
          const [L, k, x0] = regressionResult.coefficients
          return L / (1 + Math.exp(-k * (timestamp - x0)))
        }
        return null
      }

      case 'sinusoidal': {
        // For sinusoidal regression: y = A * sin(B * x + C) + D
        if (regressionResult.coefficients.length >= 4) {
          const [A, B, C, D] = regressionResult.coefficients
          return A * Math.sin(B * timestamp + C) + D
        }
        return null
      }

      default:
        return null
    }
  })
}
